import { Injectable } from '@angular/core';
import { InterpolationService, UTCDate, PaletteDescriptor } from 'map-wald';
import { WMSClientService, formatDate, indexExpression, timeParameterExpression, TerrakioLayerMin } from 'terrakio';
import * as R from 'ramda';
import { forkJoin, map, Observable, of, switchAll, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { ANOMALY_PALETTE, DataMode, DATA_MODE_CLASSES, FlattenedLayerDescriptor, LayerStyling, OverlaySelection, TimePeriod, WMSSelection, getAggregation, AggregationFunction } from './data';
import { LayersService } from './layers.service';
import { PaletteUtils, DataModeUtils, TimePeriodUtils, TimesteppedDataOperator } from './utils';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class WMSParameterService {

  constructor(private layers:LayersService,private client:WMSClientService, private http:HttpClient) { }

  getParameters(selection: OverlaySelection,timeperiod:TimePeriod,opacityPc:number): Observable<WMSSelection> {
    let attribution = selection.layer.attribution || 'MISSING ATTRIBUTION';
    if(selection.layer.attributionURL){
      attribution = `<a href="${selection.layer.attributionURL}">${attribution}</a>`;
    }
    const options: any = {
      opacity: opacityPc * 0.01,
      updateWhenIdle: true,
      updateWhenZooming: false,
      updateInterval: 500,
      attribution: attribution
    };

    let params$ = of(options);

    if(selection.flattened.source==='terrakio'){
      params$ = forkJoin([params$,this.terrakioOptions(selection,timeperiod)]).pipe(
        map(([params,terrakioOptions])=>{
          if(!terrakioOptions?.expression){
            return null;
          }
          return Object.assign({},params,terrakioOptions);
        })
      );
    } else {
      params$ = params$.pipe(
        map(params=>{
          return Object.assign({},params,{
            layer:selection.flattened.variable ? selection.flattened.variable : selection.flattened.variables[0]
          });
        })
      );
    }

    params$ = params$.pipe(
      map(params=>{
        if(!params){
          return null;
        }
        return InterpolationService.interpolateAll(Object.assign({}, params, selection.flattened.mapParams || {}),
                                                   this.interpolationSubstitutions(timeperiod));
      }));

    return params$.pipe(
      map(params=>{
        if(!params){
          return of({
            tileID: null,
            tileURL: null,
            url:null,
            params:null
          });
        }

        const KEY_PARAMS = ['opacity','cmap','cmap_bounds','expression','vmin','vmax'];
        const keyParamEntries = Object.entries(params).filter(([k,v])=>KEY_PARAMS.includes(k)&&v!=null);
        const keyParams:any = {};
        keyParamEntries.forEach(([k,v])=>{
          keyParams[k] = v;
        });
        const tileServer = selection.layer.tileserver || environment.terrakioTiles;
        return this.http.post(`${tileServer}/register`,{params:keyParams}).pipe(
          map((response:any)=>{
            const tileID:string = response.id;
            const tileURL = `${tileServer}/get_tile/${tileID}/{z}/{x}/{y}`;
            return {
              tileID,
              tileURL,
              url: this.mapUrl(selection,timeperiod),
              params
            };
          })
        );
      }),
      switchAll()
    );
  }

  terrakioOptions(selection: OverlaySelection, timeperiod: TimePeriod): Observable<any> {
    const layer = selection.flattened;
    const result:any = {...this.styleOptions(layer,selection.dataMode,timeperiod)};

    const expression$ = this.buildExpression(layer,selection.dataMode,timeperiod);

    return expression$.pipe(
      map(expression=>{
        return {...result,expression};
      }),
      // tap(result=>{
      //   ['expression','cmap','cmap_bounds','vmin','vmax'].forEach(k=>{
      //     if(result[k]){
      //       console.log(`Terrakio ${k}=${result[k]}`);
      //     }
      //   });
      // })
    );
  }

  styleOptions(layer:FlattenedLayerDescriptor,dataMode:DataMode,timeperiod:TimePeriod):any {
    const result:any = {};
    const styles = layer.gridStyles;
    let classes = PaletteUtils.effectiveClasses(styles,TimePeriodUtils.durationInMonths(timeperiod));
    result.cmap=paletteName(styles.palette,dataMode,styles.reverseAnomalyPalette);

    if((dataMode===DataMode.ABS_MEAN)||(dataMode===DataMode.TREND)){
      const scale = (dataMode===DataMode.ABS_MEAN) ? 1 : (styles.trendScale || 0.02);
      const anomalyMax = scale * (styles.anomalyLimit??((classes[classes.length-1]||classes[classes.length-2])*0.5));
      result.vmin = -anomalyMax;
      result.vmax =  anomalyMax;
    } else {
      classes = DATA_MODE_CLASSES[dataMode] || classes;
      classes = classes.map((b,i)=>{
        if(b===null){
          return (i===0?classes[1]:classes[i-1])*10;
        }
        return b;
      });
      result.cmap_bounds=classes.join(',');
    }
    return result;
  }

  buildExpression(layer: FlattenedLayerDescriptor, dataMode: DataMode, timeperiod: TimePeriod): Observable<string> {
    // console.log('\n\n--------------------- BUILDING EXPRESSION ------------------------');
    const dates$ = this.layers.availableDates(layer);
    const requestVariable = layer.script ?
      (layer.shortVariable||layer.variable) :
      `${layer.collection}.${layer.shortVariable||layer.variable}`;
    const timesteps$:Observable<WMSTimestep[]> = dates$.pipe(
      map(dates=>{
        return dates.map(d=>{
          return {
            requestVariable,
            date:d,
            genericScript:layer.script ? layer.script.join('\n'): null,
            preCalcScript:layer.script ? InterpolationService.interpolate(layer.script.join('\n'),{
              requestVariable,
              timeRequest:indexExpression(d)
            }): null,
            expression:this.client.expressionForDate(layer,d),
            timeRequest:indexExpression(d)
          } as WMSTimestep;
        })
      })
    );
    return timesteps$.pipe(
      map(timesteps=>{
        return DataModeUtils.processData(dataMode,timeperiod,getAggregation(layer),timesteps,WMS_TIMESTEP_OPERATOR);
      }),
      map(ts=>finalExpression(ts,dataMode,layer)));
  }

  private mapUrl(selection: OverlaySelection,timeperiod:TimePeriod): string {
    const layer = selection.layer;
    if (!layer) {
      return null;
    }

    if (layer.type !== 'grid') {
      return null;
    }

    if (layer.source === 'tds') {
      return `${environment.tds}/wms/${this.mapFilename(selection,timeperiod)}`;
    }

    return layer.url;
  }

  // TODO: Should this be layerSettingsFlat?
  private mapFilename(selection: OverlaySelection,timeperiod:TimePeriod): string {
    if (!selection.layer) {
      return '';
    }
    return InterpolationService.interpolate(selection.layer.filename || '', this.interpolationSubstitutions(timeperiod));
  }

  private interpolationSubstitutions(timeperiod:TimePeriod): any {
    const subs: any = {};

    const date = timeperiod.from;
    if (date) {
      subs.year = date.getUTCFullYear();
      subs.month = pad(date.getUTCMonth() + 1);
      subs.day = pad(date.getUTCDate());
    }
    return subs;
  }
}

function pad(n: number,digits?: number): string{
  digits = digits || 2;

  let result = n.toString();
  while (result.length<digits){
    result = '0' + result;
  }
  return result;
}

function monthIndex(d: UTCDate): number {
  return d.getUTCFullYear()*12 + d.getUTCMonth();
}

function fromMonthIndex(ix: number): UTCDate {
  const year = Math.floor(ix / 12);
  const month = ix % 12;
  return new Date(Date.UTC(year,month,1));
}

interface WMSTimestep {
  date:UTCDate;
  genericScript:string;
  preCalcScript:string;
  requestVariable:string;
  recurring?:boolean;
  expression:string;
  timeRequest:string;
}

function dateRangeExpression(data:WMSTimestep[],fn:string,aggregation:AggregationFunction): any {
  // console.log(`EXPRESSION:${fn}`);
  if(data.length===1){
    const ts = data[0]
    if(ts.recurring){
      // console.log(`EXPRESSION REQUEST VARIABLE:\n${ts.requestVariable}`);
      const expr = (ts.preCalcScript?.length) ? `${ts.requestVariable}_ts` : ts.expression;
      const expression = `${fn}(${expr}#(${aggregation},year))`;
      // console.log(`EXPRESSION USING RECURRING:\n${expression}`);

      return {
        expression,
        preCalcScript:data[0].preCalcScript,
        timeRequest:data[0].timeRequest
      };
    }
    // console.log(`EXPRESSION:\n${data[0].expression}`);
    return data[0].expression;
  }
  const timePeriod = {
    from:data[0].date,
    to:data[data.length-1].date
  };
  const timeRequest = timeParameterExpression(getTimeParameters(timePeriod));
  if(data[0].genericScript){
    const expression = `${fn}(${data[0].requestVariable})`;
    const preCalcScript = InterpolationService.interpolate(data[0].genericScript,{
      timeRequest,
      requestVariable:data[0].requestVariable
    });
    // console.log(`EXPRESSION:\n${expression}`);
    // console.log(`EXPRESSION PRE CALC:\n${preCalcScript}`);
    return {
      expression,
      preCalcScript,
      timeRequest
    };
  }
  const expression = `${fn}(${data[0].requestVariable}${timeRequest})`
  // console.log(`EXPRESSION:\n${expression}`);
  return {
    expression,
    timeRequest
  };
}

function getTimeParameters(timeperiod:TimePeriod):any {
  const result:any = {
    month:timeperiod.to.getUTCMonth()+1
  };
  const duration = TimePeriodUtils.durationInMonths(timeperiod);
  if(duration>1){
    result.delta_month = R.range(1-duration,1);
  }
  result.year = [timeperiod.to.getUTCFullYear()];
  return result;
}

const WMS_TIMESTEP_OPERATOR: TimesteppedDataOperator<WMSTimestep> = {
  recurringTimePeriod: (timesteps:WMSTimestep[], timeperiod: TimePeriod): WMSTimestep => {
    const variable = timesteps[0].requestVariable;//expression.split('@')[0];
    const timeParameters = getTimeParameters(timeperiod)
    timeParameters.year = timesteps
      .filter(ts=>ts.date.getUTCMonth()===timeperiod.to.getUTCMonth())
      .map(t=>t.date.getUTCFullYear())
      .filter(yr=>(yr%2)===0)
      .filter(yr=>yr!==timeperiod.to.getUTCFullYear());

    const timeRequest = timeParameterExpression(timeParameters);
    const expression:string = `${variable}${timeRequest}`;
    const preCalcScripts:string[] = [];//R.uniq(timesteps.map(ts=>ts.preCalcScript).filter(s=>s?.length));
    if(timesteps[0].genericScript){
      const script = InterpolationService.interpolate(timesteps[0].genericScript,{
        timeRequest,
        requestVariable:`${variable}_ts`
      });
      if(!preCalcScripts.includes(script)){
        preCalcScripts.push(script);
      }
    }
    // console.log('EXPRESSION PRE CALC:\n',preCalcScripts.join('\n'));
    return {
      ...timesteps[0],
      preCalcScript:preCalcScripts.join('\n'),
      date:null,
      recurring:true,
      expression,
      timeRequest
    }
  },
  sum: function (data: WMSTimestep[]): WMSTimestep {
    return {
      ...data[0],
      date: data[0].date,
      expression: data.length>1?'('+data.map(d=>d.expression).join('+')+')':data[0].expression
    }
  },
  subtract: function (lhs: WMSTimestep, rhs: WMSTimestep): WMSTimestep {
    return mergeTimeSteps(lhs,rhs,{
      expression: `(${lhs.expression})-(${rhs.expression})`
    });
  },
  average: function (data: WMSTimestep[],aggregation:AggregationFunction): WMSTimestep {
    return {
      ...data[0],
      ...dateRangeExpression(data,'mean:time',aggregation)
    }
  },
  scale: function (data: WMSTimestep, scale: number): WMSTimestep {
    return {
      ...data,
      expression: `((${data.expression})*${scale})`
    };
  },
  divide: function (lhs: WMSTimestep, rhs: WMSTimestep): WMSTimestep {
    return mergeTimeSteps(lhs,rhs,{
      expression: `((${lhs.expression})/(${rhs.expression}))`
    });
  },
  stddev: function (data: WMSTimestep[],aggregation:AggregationFunction): WMSTimestep {
    return {
      ...data[0],
      ...dateRangeExpression(data,'std:time',aggregation)
    }
  },
  min: function (data: WMSTimestep[],aggregation:AggregationFunction): WMSTimestep {
    return {
      ...data[0],
      ...dateRangeExpression(data,'min:time',aggregation)
    }
  },
  max: function (data: WMSTimestep[],aggregation:AggregationFunction): WMSTimestep {
    return {
      ...data[0],
      ...dateRangeExpression(data,'max:time',aggregation)
    }
  },
  trend: function (data: WMSTimestep[],aggregation:AggregationFunction): WMSTimestep {
    return {
      ...data[0],
      ...dateRangeExpression(data,'trend',aggregation)
    }
  }
}

function paletteName(palette: PaletteDescriptor,mode:DataMode,reverseAnomaly:boolean): string {
  if(!palette){
    return 'Oranges_3'
  }
  if([DataMode.NORMAL,DataMode.MEAN].includes(mode)){
    const rev = palette.reverse?'_r':'';
    return `${palette.name}_${palette.count}${rev}`;
  }

  const rev = reverseAnomaly?'_r':'';
  return `${ANOMALY_PALETTE}${rev}`;
}

function finalExpression(data:WMSTimestep,dataMode:DataMode,layer:FlattenedLayerDescriptor):string {
  if(!data){
    return null;
  }

  let expression = data.expression;
  let mask:string;
  if(layer.masks){
    if(dataMode===DataMode.NORMAL){
      mask = layer.masks.normal;
    } else {
      mask = layer.masks.anomaly;
    }
  }

  if(mask){
    expression = `product = ${expression}\n`;
    expression += `product[${mask}]`;
  }

  if(data.preCalcScript?.length){
    return data.preCalcScript + '\n' + expression;
  }
  return expression;
}

function combinePreCalc(lhs:WMSTimestep,rhs:WMSTimestep):string {
  const lhsScript = lhs.preCalcScript||'';
  const rhsScript = rhs.preCalcScript||'';
  if(lhsScript.includes(rhsScript)){
    return lhsScript;
  }
  if(rhsScript.includes(lhsScript)){
    return rhsScript;
  }
  return [lhsScript,rhsScript].join('\n');
}

function mergeTimeSteps(lhs:WMSTimestep,rhs:WMSTimestep,overrides:any={}):WMSTimestep {
  const result = {
    ...lhs,
    preCalcScript:combinePreCalc(lhs,rhs),
    ...overrides
  };
  // console.log('EXPRESSION MERGE TIMESTEP PRECALC\n',result.preCalcScript);
  // console.log('EXPRESSION MERGE TIMESTEP\n',result.expression);
  return result;
}
